import React, { useRef } from 'react';
import { useLanguage } from './i18n/useLanguage';
import { legalCopy } from './i18n/legal';
import { getTerms } from './i18n/terms';
import PageMetadata from './PageMetadata';
import SectionLabel from './SectionLabel';
import { SiteHeader, SiteFooter, IconSprite, Arrow } from './SiteChrome';
import './LegalPages.css';

const FIXED_LINKS = ['support@plannely.app', 'https://plannely.app/privacy-policy', 'https://plannely.app/terms', 'https://plannely.app'];
const escapeRegExp = text => text.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

function TermsText({ children, privacyPhrases }) {
  const pattern = new RegExp('(' + [...FIXED_LINKS, ...privacyPhrases].map(escapeRegExp).join('|') + ')', 'g');
  return <>{children.split(pattern).map((text, index) => {
    if (text === 'support@plannely.app') return <a key={index} href="mailto:support@plannely.app">{text}</a>;
    if (FIXED_LINKS.includes(text)) return <a key={index} href={text}>{text}</a>;
    if (privacyPhrases.includes(text)) return <a key={index} href="/privacy-policy">{text}</a>;
    return text;
  })}</>;
}

const sectionId = index => 'terms-section-' + (index + 1);
const numbered = (section, index) => (index + 1) + '. ' + section.title;

export default function TermsOfUse() {
  const [language, changeLanguage] = useLanguage();
  const contents = useRef(null);
  const copy = legalCopy(language);
  const terms = getTerms(language);
  const text = value => <TermsText privacyPhrases={terms.privacyPhrases}>{value}</TermsText>;
  const toc = () => <nav className="legal-toc" aria-label={copy.contents} lang={terms.lang}>
    {terms.sections.map((section, index) => <a key={sectionId(index)} href={'#' + sectionId(index)} onClick={() => { if (contents.current) contents.current.open = false; }}>{numbered(section, index)}</a>)}
  </nav>;

  return <div className="plannely-layout">
    <PageMetadata language={language} title={'Plannely — ' + terms.title} description={terms.description} url="https://plannely.app/terms" />
    <IconSprite />
    <div className="wrap" id="top">
      <SiteHeader language={language} onLanguageChange={changeLanguage} />
      <main className="legal-main">
        <header className="legal-intro" lang={terms.lang}>
          <SectionLabel>Plannely · SEOTIV</SectionLabel>
          <h1>{terms.title}</h1>
          <p>{terms.description}</p>
          <div className="legal-meta">
            <p>{terms.effectiveLabel} <strong>{terms.effectiveDate}</strong></p>
            <p>{terms.appLabel} <strong>Plannely</strong></p>
          </div>
        </header>
        <details className="legal-toc-mobile" ref={contents}><summary>{copy.contents}<Arrow /></summary>{toc()}</details>
        <div className="legal-body">
          <aside className="legal-sidebar"><h2>{copy.contents}</h2>{toc()}</aside>
          <article className="legal-document" lang={terms.lang}>
            {terms.sections.map((section, index) => <section className="legal-section" id={sectionId(index)} key={sectionId(index)}>
              <h2>{numbered(section, index)}</h2>
              {section.body.map((block, blockIndex) => Array.isArray(block)
                ? <ul key={blockIndex}>{block.map((item, itemIndex) => <li key={itemIndex}>{text(item)}</li>)}</ul>
                : <p key={blockIndex}>{text(block)}</p>)}
            </section>)}
          </article>
        </div>
        <a className="legal-back" href="/"><Arrow />{copy.back}</a>
      </main>
      <SiteFooter language={language} currentPage="terms" />
    </div>
  </div>;
}
